import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import systemTagsService, {
  type CreateSystemTagData,
  type UpdateSystemTagData
} from '@/services/systemTags.service';

/**
 * Organizasyonun tüm system tag'lerini getir
 */
export function useOrganizationSystemTags(organizationId: string) {
  return useQuery({
    queryKey: ['system-tags', 'organization', organizationId],
    queryFn: () => systemTagsService.getOrganizationTags(organizationId),
    enabled: !!organizationId,
  });
}

/**
 * Ticket'ın system tag'lerini getir
 */
export function useTicketSystemTags(ticketId: string) {
  return useQuery({
    queryKey: ['system-tags', 'ticket', ticketId],
    queryFn: () => systemTagsService.getTicketTags(ticketId),
    enabled: !!ticketId,
  });
}

/**
 * En çok kullanılan tag'leri getir
 */
export function useTopSystemTags(organizationId: string, limit: number = 10) {
  return useQuery({
    queryKey: ['system-tags', 'top', organizationId, limit],
    queryFn: () => systemTagsService.getTopTags(organizationId, limit),
    enabled: !!organizationId,
  });
}

/**
 * Yeni system tag oluştur
 */
export function useCreateSystemTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSystemTagData) => systemTagsService.createTag(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['system-tags', 'organization', variables.organizationId] });
    },
  });
}

/**
 * System tag güncelle
 */
export function useUpdateSystemTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tagId, data }: { tagId: string; data: UpdateSystemTagData }) =>
      systemTagsService.updateTag(tagId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-tags'] });
    },
  });
}

/**
 * System tag sil
 */
export function useDeleteSystemTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tagId: string) => systemTagsService.deleteTag(tagId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-tags'] });
    },
  });
}

/**
 * Ticket'a system tag ata
 */
export function useAssignSystemTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ticketId, systemTagId, confidenceScore }: {
      ticketId: string;
      systemTagId: string;
      confidenceScore?: number;
    }) => systemTagsService.assignTagToTicket(ticketId, systemTagId, confidenceScore),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['system-tags', 'ticket', variables.ticketId] });
    },
  });
}

/**
 * Ticket'tan system tag kaldır
 */
export function useRemoveSystemTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ticketId, systemTagId }: { ticketId: string; systemTagId: string }) =>
      systemTagsService.removeTagFromTicket(ticketId, systemTagId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['system-tags', 'ticket', variables.ticketId] });
    },
  });
}

/**
 * AI ile ticket analizi ve tag atama
 */
export function useAnalyzeTicketTags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ticketId, content, subject, organizationId, apiKey }: {
      ticketId: string;
      content: string;
      subject: string;
      organizationId: string;
      apiKey: string;
    }) => systemTagsService.analyzeTicket(ticketId, content, subject, organizationId, apiKey),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['system-tags', 'ticket', variables.ticketId] });
      queryClient.invalidateQueries({ queryKey: ['system-tags', 'organization', variables.organizationId] });
    },
  });
}
